'use client';

import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  IconFileTypePdf,
  IconFileTypeDocx,
  IconLink,
  IconExternalLink,
  IconEye,
  IconCopy,
} from '@tabler/icons-react';
import type { ProjectDocument } from '@/lib/projects/project-types';
import { formatDateNL } from '@/lib/projects/project-utils';

interface ProjectDocumentsSectionProps {
  documents?: ProjectDocument[];
  projectTitle: string;
  onPreview?: (doc: ProjectDocument) => void;
}

function DocTypeIcon({ type }: { type: ProjectDocument['type'] }) {
  if (type === 'pdf') return <IconFileTypePdf className="size-4 shrink-0 text-red-500" />;
  if (type === 'docx') return <IconFileTypeDocx className="size-4 shrink-0 text-blue-500" />;
  return <IconLink className="size-4 shrink-0 text-muted-foreground" />;
}

function docTypeBadgeVariant(type: ProjectDocument['type']): 'destructive' | 'secondary' | 'outline' {
  if (type === 'pdf') return 'destructive';
  if (type === 'docx') return 'secondary';
  return 'outline';
}

const docTypeLabel: Record<ProjectDocument['type'], string> = {
  pdf: 'PDF',
  docx: 'DOCX',
  link: 'Link',
};

export function ProjectDocumentsSection({
  documents,
  onPreview,
}: ProjectDocumentsSectionProps) {
  const handleCopyUrl = async (doc: ProjectDocument) => {
    try {
      await navigator.clipboard.writeText(doc.url);
      toast.success('Document link copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const canPreview = (doc: ProjectDocument) => doc.type === 'pdf' || doc.type === 'docx';

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold tracking-tight">Documents</h3>

      {!documents || documents.length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents available.</p>
      ) : (
        <div className="space-y-0">
          {documents.map((doc, idx) => (
            <div key={doc.id}>
              {idx > 0 && <Separator className="my-0" />}
              <div className="flex items-start gap-3 py-3">
                {/* Icon + info */}
                <div className="mt-0.5">
                  <DocTypeIcon type={doc.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium leading-snug">{doc.title}</span>
                    <Badge
                      variant={docTypeBadgeVariant(doc.type)}
                      className="text-[10px] px-1.5 py-0 h-4"
                    >
                      {docTypeLabel[doc.type]}
                    </Badge>
                  </div>
                  {(doc.source || doc.publishedAt) && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {doc.source}
                      {doc.source && doc.publishedAt && ' · '}
                      {doc.publishedAt && formatDateNL(doc.publishedAt)}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {canPreview(doc) && onPreview && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onPreview(doc)}
                      aria-label={`Preview ${doc.title}`}
                      title="Preview"
                    >
                      <IconEye className="size-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleCopyUrl(doc)}
                    aria-label={`Copy link for ${doc.title}`}
                    title="Copy link"
                  >
                    <IconCopy className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    asChild
                    aria-label={`Open ${doc.title} in new tab`}
                    title="Open in new tab"
                  >
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                      <IconExternalLink className="size-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
